/**
 * Mercado de ALZ — modal de pagamento Pix (sandbox desligado).
 *
 * Abre com o que o /rmt/buy devolveu (QR + copia-e-cola) ou reabre um pedido
 * de "Meus pedidos" (busca o QR de novo). Faz polling de
 * GET /rmt/order/{id}/payment a cada 4s: o proprio polling confirma o Pix na
 * EFI e entrega o alz, entao a tela fecha sozinha em "pago".
 */
import { useEffect, useRef, useState } from 'react';
import { Copy, Check, Loader2, X, QrCode, CheckCircle2, AlertCircle, Mail, Ban } from 'lucide-react';
import { getOrderPayment, cancelOrder, type RmtPayment } from '@/shared/services/rmtService';
import { useToast } from '@/shared/contexts/ToastContext';

const POLL_MS = 4000;

const fmtBrl = (n: number) => (n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtAlz = (n: number) => (n ?? 0).toLocaleString('pt-BR');
const fmtTimer = (s: number) =>
  s <= 0 ? '00:00' : `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

interface Props {
  orderId: number;
  /** dados que ja' vieram do /buy (evita o 1o fetch) */
  initial?: Partial<RmtPayment> | null;
  onClose: () => void;
  onChanged: () => void;
}

export default function RmtPixDialog({ orderId, initial, onClose, onChanged }: Props) {
  const { notify } = useToast();
  const [pay, setPay] = useState<RmtPayment | null>(
    initial && initial.qrCodePayload ? ({ orderId, status: 'awaiting_payment', ...initial } as RmtPayment) : null);
  const [remaining, setRemaining] = useState<number>(initial?.expiresIn ?? 0);
  const [copied, setCopied] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const done = useRef(false);

  // 1) sem QR em maos (reabrindo pelo historico): busca com imagem
  useEffect(() => {
    if (pay) return;
    getOrderPayment(orderId, true)
      .then(p => { setPay(p); setRemaining(p.expiresIn ?? 0); })
      .catch((e: any) => { notify('error', e?.detail || 'Pedido indisponível'); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // 2) polling
  const status = pay?.status;
  useEffect(() => {
    if (status !== 'awaiting_payment') return;
    const t = setInterval(async () => {
      try {
        const p = await getOrderPayment(orderId);
        if (p.status !== 'awaiting_payment') {
          setPay(prev => ({ ...(prev as RmtPayment), ...p }));
          if (!done.current) {
            done.current = true;
            if (p.status === 'completed') notify('success', 'Pagamento confirmado! ALZ enviado pelo correio.');
            onChanged();
          }
        }
      } catch { /* transitorio */ }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [status, orderId, notify, onChanged]);

  // 3) contagem
  useEffect(() => {
    if (status !== 'awaiting_payment') return;
    const t = setInterval(() => setRemaining(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [status]);

  const copy = async () => {
    if (!pay?.qrCodePayload) return;
    try {
      await navigator.clipboard.writeText(pay.qrCodePayload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { notify('error', 'Não foi possível copiar'); }
  };

  const cancel = async () => {
    setCanceling(true);
    try {
      const p = await cancelOrder(orderId);
      setPay(prev => ({ ...(prev as RmtPayment), ...p }));
      done.current = true;
      notify(p.status === 'completed' ? 'success' : 'info',
        p.status === 'completed' ? 'O Pix já tinha sido pago — ALZ entregue!' : 'Pedido cancelado.');
      onChanged();
    } catch (e: any) {
      notify('error', e?.detail || 'Erro ao cancelar');
    } finally { setCanceling(false); }
  };

  const st = pay?.status;
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-md" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
           onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="rmt-card rounded-2xl p-7 max-w-md w-full relative" onClick={e => e.stopPropagation()}>
          <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white cursor-pointer">
            <X size={20} />
          </button>

          <div className="text-center mb-5">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#f0c86f]/10 border border-[#f0c86f]/25
                            flex items-center justify-center mb-3">
              <QrCode size={28} className="text-[#f0c86f]" />
            </div>
            <h3 className="text-xl font-black text-white">Pagar com Pix</h3>
            {pay && (
              <p className="text-sm text-white/65 mt-1">
                <b className="text-white">{fmtAlz(pay.alzAmount)} ALZ</b>
                {pay.charName && <> para <b className="text-[#8fb9de]">{pay.charName}</b></>}
                <span className="block text-lg font-black text-[#f0c86f] mt-1">{fmtBrl(pay.priceBrl)}</span>
              </p>
            )}
            <p className="text-[10px] text-white/35 mt-1 font-mono">Pedido #{orderId}</p>
          </div>

          {!pay && (
            <div className="flex items-center justify-center py-12 text-white/60">
              <Loader2 size={26} className="animate-spin text-[#8fb9de]" />
              <span className="ml-3 text-sm">Carregando QR…</span>
            </div>
          )}

          {st === 'completed' && (
            <div className="flex flex-col items-center py-8 text-center">
              <CheckCircle2 size={52} className="text-emerald-400 mb-3" />
              <p className="text-lg font-black text-white">Pagamento confirmado!</p>
              <p className="text-sm text-white/65 mt-1 flex items-center gap-1.5">
                <Mail size={14} /> O ALZ chegou no correio do personagem.
              </p>
              <button onClick={onClose}
                      className="mt-5 px-6 py-2 rounded-lg bg-emerald-500 text-[#04140c] font-black text-sm cursor-pointer">
                Fechar
              </button>
            </div>
          )}

          {(st === 'expired' || st === 'cancelled') && (
            <div className="flex flex-col items-center py-8 text-center">
              <AlertCircle size={40} className="text-orange-400 mb-3" />
              <p className="text-sm text-white/80">
                {st === 'expired' ? 'O QR venceu e a reserva foi liberada.' : 'Pedido cancelado.'} Faça um novo pedido quando quiser.
              </p>
              <button onClick={onClose}
                      className="mt-4 px-5 py-2 rounded-lg bg-white/10 text-white text-sm hover:bg-white/15 cursor-pointer">
                Fechar
              </button>
            </div>
          )}

          {st === 'paid_no_stock' && (
            <div className="flex flex-col items-center py-8 text-center">
              <AlertCircle size={40} className="text-amber-400 mb-3" />
              <p className="text-sm text-white/80">
                Recebemos o seu Pix depois do QR vencer e o ALZ já tinha saído da oferta.
                A equipe vai devolver o valor — abra um ticket citando o pedido #{orderId}.
              </p>
            </div>
          )}

          {st === 'awaiting_payment' && pay && (
            <>
              {pay.qrCodeImage && (
                <div className="flex justify-center mb-4">
                  <div className="bg-white p-3 rounded-xl shadow-[0_0_40px_rgba(240,200,111,0.15)]">
                    <img src={pay.qrCodeImage} alt="QR Code Pix" width={210} height={210} className="block" />
                  </div>
                </div>
              )}
              <p className="text-[10px] text-white/55 mb-1 uppercase tracking-wider font-bold">Pix copia e cola</p>
              <div className="flex gap-2 mb-4">
                <input readOnly value={pay.qrCodePayload || ''}
                       className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-white/5 text-white text-xs font-mono truncate border border-white/10" />
                <button onClick={copy}
                        className="px-3 py-2 rounded-lg bg-[#8fb9de]/15 text-[#8fb9de] hover:bg-[#8fb9de]/25 flex items-center gap-1 text-sm font-bold cursor-pointer">
                  {copied ? <Check size={16} /> : <Copy size={16} />}{copied ? 'Copiado' : 'Copiar'}
                </button>
              </div>
              <div className="flex items-center justify-between text-xs text-white/65 border-t border-white/10 pt-3">
                <span className="flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Aguardando pagamento…</span>
                <span className="font-mono">Vence em {fmtTimer(remaining)}</span>
              </div>
              <p className="text-[11px] text-white/45 mt-3 leading-snug text-center">
                O ALZ fica reservado para você até o QR vencer. Assim que o Pix cair, ele vai
                pelo correio do jogo — confirmação automática.
              </p>
              <button onClick={cancel} disabled={canceling}
                      className="mt-4 w-full py-2 rounded-lg text-xs font-bold text-red-300/80 hover:text-red-300
                                 hover:bg-red-500/10 flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50">
                {canceling ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />} Desistir do pedido
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
